/**
 * useCapturePersistence — draft persistence with crash recovery and resume-after-auth.
 *
 * Automatically saves capture form state to IndexedDB (debounced) so that:
 * - If the app crashes or is killed mid-entry, the user can resume on next open.
 * - If the auth session expires and the user re-authenticates, the draft is restored.
 * - On successful submission, the draft is cleared.
 *
 * Storage strategy:
 * - Uses appShellStore (IndexedDB via Dexie, encrypted) — works identically on
 *   PWA, Capacitor native, and Tauri desktop. No platform-specific code paths.
 * - Drafts are namespaced per user+client to avoid cross-contamination.
 * - Drafts have a TTL of 24 hours — stale drafts are automatically discarded.
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '@/lib/authService';
import appShellStore from '@/lib/offline/appShellStore';
import {
  type CaptureFormData,
  getEntryType,
  fromRecordString,
  toRecordString,
} from './captureSchema';
import type { ChildEntryType } from '../../types';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CaptureDraft {
  /** Schema version for forward compatibility. */
  v: 1;
  /** ISO timestamp when the draft was last saved. */
  savedAt: string;
  /** User ID that created the draft (for namespacing). */
  userId: string;
  /** Client ID that created the draft (for namespacing). */
  clientId: string;
  /** The selected entry type when the draft was saved. */
  entryType: ChildEntryType;
  /** Form data as a Record (serialized from CaptureFormData). */
  formData: Record<string, string>;
  /** Receipt photo data URLs (base64). */
  receiptPhotos: string[];
  /** Detected currency at time of save. */
  detectedCurrency: string;
  /** Whether the user had linked to a work item. */
  linkToWorkItem: boolean;
  /** Whether the expense was job-linked. */
  isJobLinkedExpense: boolean;
  /** Whether the user had linked to vehicle. */
  linkToVehicle: boolean;
  /** Expense line items (for procurement entries). */
  expenseLineItems: Array<{
    description: string;
    quantity: number;
    unit_price: number;
    line_total?: number | null;
    tax_rate?: number | null;
    tax_amount?: number | null;
    expense_account_code?: string | null;
    allocation_type?: string | null;
  }>;
}

export interface UseCapturePersistenceParams {
  /** Whether the capture form is currently open. */
  isOpen: boolean;
  /** Current form data (CaptureFormData discriminated union). */
  formData: CaptureFormData;
  /** Receipt photos (base64 data URLs). */
  receiptPhotos: string[];
  /** Detected currency. */
  detectedCurrency: string;
  /** Whether linked to work item. */
  linkToWorkItem: boolean;
  /** Whether expense is job-linked. */
  isJobLinkedExpense: boolean;
  /** Whether linked to vehicle. */
  linkToVehicle: boolean;
  /** Expense line items. */
  expenseLineItems: Array<{
    description: string;
    quantity: number;
    unit_price: number;
    line_total?: number | null;
    tax_rate?: number | null;
    tax_amount?: number | null;
    expense_account_code?: string | null;
    allocation_type?: string | null;
  }>;
}

export interface RestoredDraft {
  formData: CaptureFormData;
  entryType: ChildEntryType;
  receiptPhotos: string[];
  detectedCurrency: string;
  linkToWorkItem: boolean;
  isJobLinkedExpense: boolean;
  linkToVehicle: boolean;
  expenseLineItems: CaptureDraft['expenseLineItems'];
  /** True when the draft had only photos and no form data — photos must be recaptured. */
  isPhotoOnly: boolean;
}

export interface UseCapturePersistenceReturn {
  /** Whether a recoverable draft was detected on mount. */
  hasRecoverableDraft: boolean;
  /** The recovered draft, if any. */
  recoveredDraft: CaptureDraft | null;
  /** True when the recovered draft has only photos and no form data. */
  draftIsPhotoOnly: boolean;
  /** Restore the recovered draft into the form. Returns the form data + metadata. */
  restoreDraft: () => RestoredDraft | null;
  /** Discard the recovered draft without restoring. */
  discardDraft: () => void;
  /** Explicitly clear the persisted draft (called on successful submission). */
  clearDraft: () => void;
  /** Manually save the current state as a draft. */
  saveDraft: () => void;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const DRAFT_ENTITY_PREFIX = 'capture_draft_';
const DRAFT_DISMISSED_ENTITY = 'capture_draft_dismissed';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const DEBOUNCE_MS = 800;
const MIN_FORM_DATA_KEYS = 1; // At least one field besides 'type' must be set

// ── Helpers ────────────────────────────────────────────────────────────────────

interface DraftKey {
  clientId: number;
  entityId: string;
}

function getDraftKey(): DraftKey | null {
  const user = authService.getUser();
  if (!user?.id) return null;
  const clientId = authService.getSessionClientId() ?? 0;
  if (!clientId) return null;
  return {
    clientId,
    entityId: `${DRAFT_ENTITY_PREFIX}${user.id}`,
  };
}

function isDraftFresh(draft: CaptureDraft): boolean {
  const savedAt = new Date(draft.savedAt).getTime();
  if (Number.isNaN(savedAt)) return false;
  return Date.now() - savedAt < DRAFT_TTL_MS;
}

function hasMeaningfulContent(params: UseCapturePersistenceParams): boolean {
  const record = toRecordString(params.formData);
  // Must have at least one non-type field with a value
  const meaningfulKeys = Object.keys(record).filter(k => k !== 'type' && record[k]?.trim());
  if (meaningfulKeys.length >= MIN_FORM_DATA_KEYS) return true;
  // Or receipt photos
  if (params.receiptPhotos.length > 0) return true;
  // Or expense line items
  if (params.expenseLineItems.length > 0) return true;
  return false;
}

/**
 * Check if a draft has ONLY photos and no meaningful form data.
 * Such drafts require the user to recapture the photo.
 */
function isPhotoOnlyDraft(draft: CaptureDraft): boolean {
  const meaningfulKeys = Object.keys(draft.formData).filter(
    k => k !== 'type' && draft.formData[k]?.trim(),
  );
  return meaningfulKeys.length === 0 &&
    draft.expenseLineItems.length === 0 &&
    draft.receiptPhotos.length > 0;
}

async function readDismissedTimestamp(key: DraftKey): Promise<number | null> {
  try {
    const ts = await appShellStore.getJson<number>(key.clientId, DRAFT_DISMISSED_ENTITY);
    return typeof ts === 'number' ? ts : null;
  } catch {
    return null;
  }
}

async function writeDismissedTimestamp(key: DraftKey, timestamp: number): Promise<void> {
  try {
    await appShellStore.upsertJson(key.clientId, DRAFT_DISMISSED_ENTITY, timestamp);
  } catch { /* ignore */ }
}

async function clearDismissedTimestamp(key: DraftKey): Promise<void> {
  try {
    await appShellStore.deleteJson(key.clientId, DRAFT_DISMISSED_ENTITY);
  } catch { /* ignore */ }
}

function serializeDraft(params: UseCapturePersistenceParams): CaptureDraft | null {
  const user = authService.getUser();
  if (!user?.id) return null;

  const entryType = getEntryType(params.formData);
  const record = toRecordString(params.formData);

  return {
    v: 1,
    savedAt: new Date().toISOString(),
    userId: String(user.id),
    clientId: user.client_id != null ? String(user.client_id) : 'unknown',
    entryType,
    formData: record,
    receiptPhotos: params.receiptPhotos,
    detectedCurrency: params.detectedCurrency,
    linkToWorkItem: params.linkToWorkItem,
    isJobLinkedExpense: params.isJobLinkedExpense,
    linkToVehicle: params.linkToVehicle,
    expenseLineItems: params.expenseLineItems,
  };
}

async function readDraft(key: DraftKey): Promise<CaptureDraft | null> {
  try {
    const draft = await appShellStore.getJson<CaptureDraft>(key.clientId, key.entityId);
    if (!draft) return null;
    if (draft.v !== 1) return null;
    if (!isDraftFresh(draft)) {
      // Auto-clean stale draft
      await appShellStore.deleteJson(key.clientId, key.entityId).catch(() => {});
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

async function writeDraft(key: DraftKey, draft: CaptureDraft): Promise<void> {
  try {
    await appShellStore.upsertJson(key.clientId, key.entityId, draft);
  } catch {
    // Storage full or unavailable — silently ignore
  }
}

async function removeDraft(key: DraftKey): Promise<void> {
  try {
    await appShellStore.deleteJson(key.clientId, key.entityId);
  } catch { /* ignore */ }
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useCapturePersistence(
  params: UseCapturePersistenceParams,
): UseCapturePersistenceReturn {
  const [recoveredDraft, setRecoveredDraft] = useState<CaptureDraft | null>(null);
  const [draftIsPhotoOnly, setDraftIsPhotoOnly] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const hasRestoredRef = useRef(false);
  const discardedRef = useRef(false);
  const submittedRef = useRef(false);

  // ── On open: check for recoverable draft ──────────────────────────────────
  useEffect(() => {
    if (!params.isOpen) return;
    // Only check once per open cycle
    if (hasRestoredRef.current) return;

    const key = getDraftKey();
    if (!key) return;

    let cancelled = false;
    void (async () => {
      const [draft, dismissedTs] = await Promise.all([
        readDraft(key),
        readDismissedTimestamp(key),
      ]);
      if (cancelled) return;
      if (draft) {
        // If the draft was saved BEFORE the user dismissed a previous draft,
        // it's stale — delete it and don't show.
        if (dismissedTs != null) {
          const draftSavedAt = new Date(draft.savedAt).getTime();
          if (draftSavedAt <= dismissedTs) {
            void removeDraft(key);
            return;
          }
        }
        setDraftIsPhotoOnly(isPhotoOnlyDraft(draft));
        setRecoveredDraft(draft);
      }
    })();

    return () => { cancelled = true; };
  }, [params.isOpen]);

  // ── Reset discard/submission flags for new open cycle ──────────────────────
  // Deferred via setTimeout(0) so that stale saveDraft cleanups (which capture
  // old params from the previous render) run BEFORE the flags are reset.
  // Without this, discarding a draft → closing → reopening with a different
  // entry type causes the stale saveDraft to write old form data as a new draft
  // because discardedRef is reset synchronously in the open effect.
  useEffect(() => {
    if (!params.isOpen) return;
    const timer = setTimeout(() => {
      discardedRef.current = false;
      submittedRef.current = false;
    }, 0);
    return () => clearTimeout(timer);
  }, [params.isOpen]);

  // ── Debounced auto-save while form is open ────────────────────────────────
  const saveDraft = useCallback(() => {
    if (!params.isOpen) return;
    if (discardedRef.current) return;
    if (submittedRef.current) return;
    if (!hasMeaningfulContent(params)) return;

    const key = getDraftKey();
    if (!key) return;

    const draft = serializeDraft(params);
    if (!draft) return;

    const serialized = JSON.stringify(draft);
    if (serialized === lastSavedRef.current) return; // Skip if unchanged
    lastSavedRef.current = serialized;

    void writeDraft(key, draft);
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!params.isOpen) return;
    // Don't auto-save if we're showing the recovery prompt
    if (recoveredDraft) return;
    // Don't auto-save after user discarded the draft
    if (discardedRef.current) return;
    // Don't auto-save after successful submission
    if (submittedRef.current) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      saveDraft();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [params.isOpen, recoveredDraft, saveDraft]);

  // ── Resume-after-auth: listen for auth state changes ──────────────────────
  useEffect(() => {
    if (!params.isOpen) return;

    const handleAuthChange = () => {
      const key = getDraftKey();
      if (!key) return;
      void (async () => {
        const draft = await readDraft(key);
        if (draft && !hasRestoredRef.current) {
          setRecoveredDraft(draft);
        }
      })();
    };

    authService.addEventListener('authStateChanged', handleAuthChange);
    return () => {
      authService.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, [params.isOpen]);

  // ── Restore draft ──────────────────────────────────────────────────────────
  const restoreDraft = useCallback((): RestoredDraft | null => {
    if (!recoveredDraft) return null;

    const draft = recoveredDraft;
    const entryType = draft.entryType;
    const formData = fromRecordString(entryType, draft.formData);
    const photoOnly = isPhotoOnlyDraft(draft);

    hasRestoredRef.current = true;
    setRecoveredDraft(null);
    setDraftIsPhotoOnly(false);

    // Clear the dismissed timestamp since we're restoring
    const key = getDraftKey();
    if (key) void clearDismissedTimestamp(key);

    return {
      formData,
      entryType,
      // If photo-only, don't restore stale photos — user must recapture
      receiptPhotos: photoOnly ? [] : draft.receiptPhotos,
      detectedCurrency: draft.detectedCurrency,
      linkToWorkItem: draft.linkToWorkItem,
      isJobLinkedExpense: draft.isJobLinkedExpense,
      linkToVehicle: draft.linkToVehicle,
      expenseLineItems: draft.expenseLineItems,
      isPhotoOnly: photoOnly,
    };
  }, [recoveredDraft]);

  // ── Discard draft (user declines recovery) ────────────────────────────────
  const discardDraft = useCallback(() => {
    const key = getDraftKey();
    if (key) {
      void removeDraft(key);
      // Persist dismissal timestamp so the same draft won't reappear on next open
      void writeDismissedTimestamp(key, Date.now());
    }
    lastSavedRef.current = null;
    hasRestoredRef.current = true;
    discardedRef.current = true;
    setRecoveredDraft(null);
    setDraftIsPhotoOnly(false);
  }, []);

  // ── Clear draft (on successful submission) ─────────────────────────────────
  const clearDraft = useCallback(() => {
    const key = getDraftKey();
    if (key) {
      void removeDraft(key);
      void clearDismissedTimestamp(key);
    }
    lastSavedRef.current = null;
    hasRestoredRef.current = true;
    discardedRef.current = true;
    submittedRef.current = true;
    setRecoveredDraft(null);
    setDraftIsPhotoOnly(false);
  }, []);

  // ── Reset restoration flag when form closes ────────────────────────────────
  // Note: discardedRef is NOT reset here — it's reset in the open effect to
  // prevent the save-draft cleanup from re-saving after discard/clear.
  useEffect(() => {
    if (!params.isOpen) {
      hasRestoredRef.current = false;
      lastSavedRef.current = null;
      // Defer state update to avoid cascading renders
      const timer = setTimeout(() => {
        setRecoveredDraft(null);
        setDraftIsPhotoOnly(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [params.isOpen]);

  return {
    hasRecoverableDraft: recoveredDraft !== null,
    recoveredDraft,
    draftIsPhotoOnly,
    restoreDraft,
    discardDraft,
    clearDraft,
    saveDraft,
  };
}
